import { PropsWithChildren, createContext, useContext } from 'react';
import { useHref, useNavigate } from 'react-router-dom';

import { useRouteRef } from '@backstage/core-plugin-api';

import { RouterProvider } from '@spotify-internal/encore-web';
import { extractLastNameComponentFromName } from '@spotify/confidence-core-react';
import { WorkflowInstanceFragment } from '@spotify/confidence-graphql';
import { PermissionUtils } from '@spotify/plugin-confidence-permissions-react';

import {
  getHasWorkflowLaunched,
  hasWorkflowHadState,
} from '../../helpers/workflowInstanceUtils';
import { workflowInstanceRouteRef } from '../../routes';
import { useWorkflowContext } from '../WorkflowContext';
import { WorkflowTransitionProgressProvider } from '../WorkflowTransitionProgressContext';

type WorkflowInstanceContext<T = WorkflowInstanceFragment> = {
  href: string;
  route: (subpage: string) => string;
  canEdit: boolean;
  hasHadState: (states: readonly string[]) => boolean;
  hasLaunched: boolean;
  isDraft: boolean;
  isLive: boolean;
} & WorkflowInstanceFragment &
  T;

const context = createContext<WorkflowInstanceContext | undefined>(undefined);

const useExternalAwareHref = (href: string): string => {
  const routerHref = useHref(href);
  return URL.canParse(href) || href.startsWith('//') ? href : routerHref;
};

export const WorkflowInstanceContextProvider = <T,>({
  value,
  children,
}: PropsWithChildren<{ value: WorkflowInstanceFragment & T }>) => {
  const { workflowId: workflow } = useWorkflowContext();

  const id = extractLastNameComponentFromName(value.name);

  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const navigate = useNavigate();
  const href = workflowInstanceRoute?.({
    workflow,
    id,
  });

  const canEdit = PermissionUtils.hasRelation(value, 'can_edit');

  const hasLaunched = getHasWorkflowLaunched(value);
  const hasHadState = (states: readonly string[]) =>
    hasWorkflowHadState(value, states);
  const isDraft = value.state === 'draft';
  const isLive = value.state === 'live';

  const route = (subpage: string) => `${href}/${subpage}`;

  return (
    <RouterProvider navigate={navigate} useHref={useExternalAwareHref}>
      <context.Provider
        value={{
          ...value,
          href,
          route,
          canEdit,
          hasHadState,
          hasLaunched,
          isDraft,
          isLive,
        }}
      >
        <WorkflowTransitionProgressProvider
          key={value.name}
          workflowInstance={value}
        >
          {children}
        </WorkflowTransitionProgressProvider>
      </context.Provider>
    </RouterProvider>
  );
};

export const useWorkflowInstanceContext = <
  T = WorkflowInstanceFragment,
>(): WorkflowInstanceContext<T> => {
  const value = useContext(context);

  if (!value) {
    throw new Error(
      'useWorkflowInstance must be used within a WorkflowInstanceProvider',
    );
  }

  return value as WorkflowInstanceContext<T>;
};
